import React, { useState, useRef, useEffect } from 'react';
import { IoSend } from 'react-icons/io5';
import { BsRobot } from 'react-icons/bs';
import Dialog from '../../organisms/Dialog';
import { monitoringApi } from '../../../services/monitoringApi';

interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    isLoading?: boolean;
    error?: boolean;
}

interface MonitoringChatModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const WELCOME_MESSAGE: ChatMessage = {
    id: 'welcome',
    role: 'assistant',
    content: 'Olá! Sou o assistente de monitoramento PLD. Posso consultar dados sobre classificações de risco, batches de monitoramento, histórico de execuções e divergências. Como posso ajudar?',
};

const MonitoringChatModal: React.FC<MonitoringChatModalProps> = ({ isOpen, onClose }) => {
    const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Reset chat when modal opens
    useEffect(() => {
        if (isOpen) {
            setMessages([WELCOME_MESSAGE]);
            setInput('');
            setIsLoading(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async () => {
        const question = input.trim();
        if (!question || isLoading) return;

        const userMsg: ChatMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: question,
        };

        const loadingMsg: ChatMessage = {
            id: `loading-${Date.now()}`,
            role: 'assistant',
            content: '',
            isLoading: true,
        };

        setMessages(prev => [...prev, userMsg, loadingMsg]);
        setInput('');
        setIsLoading(true);

        try {
            const response = await monitoringApi.chat(question);
            const { answer } = response.data;

            setMessages(prev =>
                prev.map(msg =>
                    msg.id === loadingMsg.id
                        ? { ...msg, content: answer, isLoading: false }
                        : msg
                )
            );
        } catch (err: unknown) {
            let errorMessage = 'Erro ao processar sua pergunta. Tente novamente.';
            if (err instanceof Error) {
                if (err.message.includes('Load Failed') || err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
                    errorMessage = 'A consulta demorou demais para responder. Tente reformular com uma pergunta mais simples ou específica.';
                } else {
                    errorMessage = err.message;
                }
            }
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === loadingMsg.id
                        ? { ...msg, content: errorMessage, isLoading: false, error: true }
                        : msg
                )
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const body = (
        <div className="flex flex-col" style={{ height: '450px' }}>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {messages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                            className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm ${
                                msg.role === 'user'
                                    ? 'bg-blue-600 text-white'
                                    : msg.error
                                        ? 'bg-red-50 text-red-700 border border-red-200'
                                        : 'bg-gray-100 text-gray-800'
                            }`}
                        >
                            {msg.isLoading ? (
                                <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    <span className="text-gray-500">Consultando...</span>
                                </div>
                            ) : (
                                <p className="whitespace-pre-wrap">{msg.content}</p>
                            )}
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-5 py-3 border-t border-gray-200 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Faça uma pergunta sobre o monitoramento..."
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 placeholder-gray-400"
                        disabled={isLoading}
                        maxLength={500}
                        aria-label="Digite sua pergunta"
                    />
                    <button
                        onClick={handleSend}
                        disabled={!input.trim() || isLoading}
                        className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        aria-label="Enviar pergunta"
                    >
                        <IoSend className="w-4 h-4" />
                    </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                    Pergunte sobre classificações, batches, histórico ou divergências do monitoramento.
                </p>
            </div>
        </div>
    );

    return (
        <Dialog
            id="monitoring-chat-modal"
            show={isOpen}
            onClose={onClose}
            header="Assistente IA — Monitoramento"
            body={body}
            size="lg"
        />
    );
};

export default MonitoringChatModal;
